import { IPageContent } from "api/interfaces/static-content.type";
import FAQMetaHead from "component/meta-header/FAQMetaHead";
import { RootState } from "interfaces/generics.type";
import Head from "next/head";
import { useSelector } from "react-redux";
import VideosMetaData from "component/meta-header/VideosMetaData";
import { getMetaData } from "../../../component/meta-header/meta-utils";
import { getRelativeImagePath } from "../../../util/helper/getRelativeImagePath";
import { getProductHeading } from "../../../util/product/get-product-heading";
import { getRatingFromReviews } from "../../../util/rating-review-helper";
import { getLinePageThumbImagePath } from "../line-page/utils";

const aggregateRatingMeta = (reviews: any) => {
  const constAggRating = getRatingFromReviews(reviews);
  if (!reviews || !reviews.length) {
    return undefined;
  }
  return {
    "@type": "AggregateRating",
    bestRating: "5",
    ratingValue: `${constAggRating}`,
    reviewCount: `${reviews.length}`,
  };
};

const getReviewMeta = (reviews: any) => {
  if (!reviews.length) {
    return undefined;
  }
  return reviews.map((review: any) => ({
    "@type": "Review",

    reviewRating: {
      "@type": "Rating",
      ratingValue: `${review.rating}`,
      bestRating: "5",
    },

    datePublished: review.createdDate,
    reviewBody: review.review,

    author: {
      "@type": "Person",
      name: review.reviewerName,
    },
  }));
};

interface IProps{
  staticContent: IPageContent;
}

function ProductLineMetaHead(props: IProps) {
  const {
    productReviews,
    staticContent,
    productsAggregation,
    minimumPrice,
    maximumPrice,
    locale,
  } = props;
  const { pageDetails, productLineImages = [], videos = [] } = staticContent;
  const { products } = productsAggregation.products;
  const { manufacturer, manufacturerImageUrl } = pageDetails || {};
  const { currentPageType } = useSelector((store: RootState) => store.utils);

  let { pageTitle = "", metaDescription = "", metaKeywords = "" } = getMetaData({ staticContent, pageType: currentPageType });

  if (!Object.keys(staticContent).length) {
    pageTitle = locale === "en" ? "Auto Parts in Canada: Auto Parts Online Canada" : "Parts Avatar : Pièces d'auto et de camion en ligne au Canada";
  }

  const manufacturerSmallImageUrl = manufacturerImageUrl;
  const thumbnails = (productLineImages.find((_: any) => _.type === "SINGLE_IMAGE")?.urls || []).filter((_: any) => _ !== "");
  const thumbnail = thumbnails && thumbnails[0] ? thumbnails[0] : "";
  const canonicalUrl = staticContent.url;

  const prodDesc = staticContent?.links?.contentIds?.filter((_: any) => _.uiDetails?.uiType === "PRODUCT_DESCRIPTION");
  let metaDescriptionJSONLD;
  if (prodDesc && prodDesc[0]) {
    metaDescriptionJSONLD = prodDesc[0].uiDetails.content.article;
  }

  const url = `https://partsavatar.ca${canonicalUrl}`;

  const image = getLinePageThumbImagePath(thumbnail);

  const microdata = {
    "@context": "http://schema.org/",
    "@type": "Product",
    name: getProductHeading(props, pageDetails),
    image, // Check if we should use this property for that
    description: metaDescriptionJSONLD || metaDescription,
    sku: canonicalUrl, // for use canonicalUrl is unique
    url,
    brand: {
      "@type": "http://schema.org/Brand",
      name: manufacturer,
      logo: getRelativeImagePath(manufacturerSmallImageUrl),
    },
    aggregateRating: aggregateRatingMeta(productReviews),
    review: getReviewMeta(productReviews),
  };

  if (products.length) {
    microdata.offers = {
      "@type": "AggregateOffer",
      priceCurrency: "CAD",
      highPrice: (maximumPrice / 100).toFixed(2),
      lowPrice: (minimumPrice / 100).toFixed(2),
      offerCount: products.length,
    };
  }

  return (
    <>
      <Head>

        <title>{pageTitle}</title>

        <link rel="canonical" href={url} />
        <link rel="alternate" hrefLang="en" href={url} />
        <link rel="alternate" hrefLang="fr" href={`https://partsavatar.ca/fr${canonicalUrl}`} />
        <link rel="alternate" hrefLang="x-default" href={url} />

        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(microdata) }}
        />

        <meta name="facebook-domain-verification" content="82cgyip0cz3a7igt87rjow5o8bf9th" />
        <meta name="description" content={metaDescription} />

        <meta name="keywords" content={metaKeywords.toLowerCase()} />

        <meta name="robots" content="all" />

        <meta property="og:locale" content="en_CA" />
        <meta property="og:locale:alternate" content="en_CA" />
        <meta property="og:locale:alternate" content="fr_CA" />

        <meta property="og:title" content={pageTitle} />

        <meta property="og:description" content={metaDescription} />

        <meta property="og:type" content="product" />

        <meta property="og:url" content={url} />

        <meta property="og:image" content={image} />
      </Head>
      <FAQMetaHead contentIds={staticContent?.links?.contentIds || []} />
      <VideosMetaData videos={videos} />
    </>
  );
}

export default ProductLineMetaHead;
